// The "Surprise Reveal" system — web mirror of
// ios/PredictSix/Models/SurpriseRevealCatalog.swift
// (docs/reveal-surprise-experience-2026-07.md).
//
// A prediction game forecasts an uncertain future, so a miss isn't "you failed,"
// it's "the future surprised us." Instead of the flat grey ✕, a wrong reveal
// draws from a ROTATING pool of little surprise moments (motif glyph + entrance
// animation + copy). Selection (see dealtSurprise):
//   1. Rare special — a ~3.5% deterministic per-(user, question) jackpot
//      (RARE_STYLES), seeded so a given miss always resolves the same rare-or-not.
//   2. Dealt daily style — otherwise, each of a day's misses is dealt a DISTINCT
//      style from a per-(user, day) shuffle of the pool (SURPRISE_STYLES + any
//      active seasonal style), so the day's misses don't repeat, vary day to day,
//      and are stable on revisit. (Legacy todaySurpriseStyle / surpriseForReveal —
//      the single day-of-year feature — kept only for back-compat.)
//
// LOCKSTEP with iOS: the style ids, labels, copy pools, daily selection, seasonal
// windows, and the FNV-1a roll hash + odds all match, so a player sees the same
// surprise on both platforms. Only the MOTIF glyph differs (iOS = SF Symbol,
// web = monochrome unicode). Some web glyphs carry a U+FE0E text-presentation
// selector to stop a browser rendering them as colour emoji on the neutral circle.
//
// GUARDRAILS (design law): neutral, never red; clever, not cruel (the future
// surprised EVERYONE); the gold correct-payoff still wins.

export const SURPRISE_STYLES = [
  {
    id: 'plot_twist',
    label: 'PLOT TWIST',
    glyph: '↻', // iOS: arrow.triangle.2.circlepath
    anim: 'ps-surprise-flip',
    copy: [
      'Plot twist — nobody saw that coming.',
      'The script flipped on everyone.',
      'The future had other plans.',
    ],
  },
  {
    id: 'curveball',
    label: 'CURVEBALL',
    glyph: '⤴', // iOS: arrow.turn.up.right
    anim: 'ps-surprise-swoop',
    copy: [
      'Curveball — the future broke the other way.',
      'That one curved on all of us.',
      'A curveball from up ahead.',
    ],
  },
  {
    id: 'world_zigged',
    label: 'THE WORLD ZIGGED',
    glyph: '⇄', // iOS: arrow.left.arrow.right
    anim: 'ps-surprise-zigzag',
    copy: [
      'You zagged — the world zigged.',
      'Everyone leaned one way; reality leaned the other.',
      'The world zigged on this one.',
    ],
  },
  {
    id: 'reroll',
    label: 'REROLL',
    glyph: '⚄', // iOS: dice.fill
    anim: 'ps-surprise-tumble',
    copy: [
      'The dice rerolled on all of us.',
      'Reality rolled again.',
      'New roll, new outcome.',
    ],
  },
  {
    id: 'signal_lost',
    label: 'SIGNAL LOST',
    glyph: '≋', // iOS: dot.radiowaves.left.and.right
    anim: 'ps-surprise-ripple',
    copy: [
      'Signal lost — the future stayed unknowable.',
      "Static — even the crowd couldn't read this one.",
      'The future kept this one fuzzy.',
    ],
  },
  {
    id: 'eclipse',
    label: 'ECLIPSE',
    glyph: '◐', // iOS: moon.fill
    anim: 'ps-surprise-eclipse',
    copy: [
      'The future kept this one in the dark.',
      'Eclipsed — nobody had the full picture.',
      'The outcome hid in the shadows.',
    ],
  },
  // ── Phase 2 expansion (6 → 10) ─────────────────────────────────────────────
  {
    id: 'overtime',
    label: 'OVERTIME',
    glyph: '↺', // iOS: clock.arrow.circlepath
    anim: 'ps-surprise-flip',
    copy: [
      'Overtime — it flipped at the buzzer.',
      'Decided in the final seconds.',
      'The late swing caught everyone.',
    ],
  },
  {
    id: 'underdog',
    label: 'UNDERDOG',
    glyph: '↗︎', // iOS: arrow.up.forward
    anim: 'ps-surprise-swoop',
    copy: [
      'The underdog had other ideas.',
      'The long shot landed.',
      'Never count out the upset.',
    ],
  },
  {
    id: 'mirage',
    label: 'MIRAGE',
    glyph: '❉', // iOS: wand.and.stars
    anim: 'ps-surprise-ripple',
    copy: [
      'A mirage — the sure thing dissolved.',
      'Looked locked. Wasn’t.',
      'The obvious answer was a mirage.',
    ],
  },
  {
    id: 'detour',
    label: 'DETOUR',
    glyph: '↪', // iOS: arrow.uturn.right
    anim: 'ps-surprise-zigzag',
    copy: [
      'Reality took the other exit.',
      'The outcome detoured.',
      'It routed around the obvious.',
    ],
  },
  // ── Phase 4 expansion (10 → 16) ──────────────────────────────────────────────
  {
    id: 'photo_finish',
    label: 'PHOTO FINISH',
    glyph: '⚑', // iOS: flag.checkered
    anim: 'ps-surprise-swoop',
    copy: [
      'A photo finish — it slipped the other way.',
      "Too close to call, until it wasn't.",
      'Decided by a hair.',
    ],
  },
  {
    id: 'gravity_check',
    label: 'GRAVITY CHECK',
    glyph: '↓', // iOS: arrow.down
    anim: 'ps-surprise-tumble',
    copy: [
      'What went up came down.',
      'Gravity had the last word.',
      'The sure thing found the floor.',
    ],
  },
  {
    id: 'tide_turned',
    label: 'TIDE TURNED',
    glyph: '≈', // iOS: water.waves
    anim: 'ps-surprise-ripple',
    copy: [
      'The tide turned late.',
      'The current reversed on everyone.',
      'It flowed the other way.',
    ],
  },
  {
    id: 'boomerang',
    label: 'BOOMERANG',
    glyph: '↩', // iOS: arrow.uturn.backward
    anim: 'ps-surprise-zigzag',
    copy: [
      'It came right back around.',
      'The outcome boomeranged.',
      'What looked gone came back the other way.',
    ],
  },
  {
    id: 'trapdoor',
    label: 'TRAPDOOR',
    glyph: '⤓', // iOS: arrow.down.to.line
    anim: 'ps-surprise-eclipse',
    copy: [
      'The floor gave way.',
      'A trapdoor under the sure thing.',
      'It dropped out from under everyone.',
    ],
  },
  {
    id: 'wrench',
    label: 'MONKEY WRENCH',
    glyph: '⚙︎', // iOS: wrench.and.screwdriver.fill
    anim: 'ps-surprise-flip',
    copy: [
      'Someone threw a wrench in it.',
      'A monkey wrench in the works.',
      'The gears turned the other way.',
    ],
  },
];

// Rare "legendary" pool — a ~3.5% jackpot on any wrong reveal (rollRareStyle).
// Distinct motif/label/copy from the daily 10 so hitting one feels special.
export const RARE_STYLES = [
  {
    id: 'quantum',
    label: 'QUANTUM',
    glyph: '⚛︎', // iOS: atom
    anim: 'ps-surprise-tumble',
    isRare: true,
    copy: [
      'Quantum outcome — it collapsed the other way.',
      'Superposition broke against everyone.',
      'The future picked the other branch.',
    ],
  },
  {
    id: 'supernova',
    label: 'SUPERNOVA',
    glyph: '✸', // iOS: sparkles
    anim: 'ps-surprise-ripple',
    isRare: true,
    copy: [
      'The future went supernova on all of us.',
      'It all scattered — nobody had this.',
      'A once-in-an-era blowup.',
    ],
  },
  {
    id: 'wildcard',
    label: 'WILDCARD',
    glyph: '♠︎', // iOS: suit.spade.fill
    anim: 'ps-surprise-flip',
    isRare: true,
    copy: [
      'Wildcard — reality drew from the bottom of the deck.',
      'The future played a wildcard.',
      'Nobody had this card.',
    ],
  },
];

// Phase 3 seasonal pool — dormant until its ET date window (seasonalStyle), then
// it overrides the daily feature for those days. Held until the calendar fires it.
export const SEASONAL_STYLES = [
  {
    id: 'spooky',
    label: 'SPOOKY',
    glyph: '☾', // iOS: theatermasks.fill
    anim: 'ps-surprise-flip',
    copy: [
      'A trick from the future.',
      'The future wore a mask.',
      'Spooky — it flipped in the dark.',
    ],
  },
  {
    id: 'timeline_forked',
    label: 'TIMELINE FORKED',
    glyph: '⋔', // iOS: arrow.triangle.branch
    anim: 'ps-surprise-zigzag',
    copy: [
      'New year, new timeline.',
      'The timeline forked at midnight.',
      'A fresh branch of the future.',
    ],
  },
  {
    id: 'april_fools',
    label: 'APRIL FOOLS',
    glyph: '☺︎', // iOS: face.smiling
    anim: 'ps-surprise-tumble',
    copy: [
      'The future pulled an April Fools.',
      'Gotcha — reality flipped it.',
      'No joke: the future zigged.',
    ],
  },
];

// ~3.5% of wrong reveals fire a rare (out of 1000). Lockstep with iOS
// rareOddsPerThousand. Low enough that a rare feels like a genuine event.
const RARE_ODDS_PER_THOUSAND = 35;

// ET (America/New_York) day-of-year, 1..366 — mirrors iOS
// Calendar.ordinality(of: .day, in: .year). CLAUDE.md rule #7 (ET is canonical).
function etMonthDayYear(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  return { m: get('month'), d: get('day'), y: get('year') };
}

export function dayOfYearET(date = new Date()) {
  const { m, d, y } = etMonthDayYear(date);
  // Days since Jan 1 of that same ET year, via UTC math to sidestep local TZ.
  return Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86400000) + 1;
}

// The active seasonal style for an ET date, or null outside every window.
// Windows (ET): Halloween Oct 25–31 · New Year Dec 31 + Jan 1 · April Fools Apr 1.
export function seasonalStyle(date = new Date()) {
  const { m, d } = etMonthDayYear(date);
  if (m === 10 && d >= 25 && d <= 31) return styleById('spooky');
  if ((m === 12 && d === 31) || (m === 1 && d === 1)) return styleById('timeline_forked');
  if (m === 4 && d === 1) return styleById('april_fools');
  return null;
}

// Today's featured surprise — seasonal window first (Phase 3), else the
// ET-day-of-year daily rotation. Mirrors SurpriseRevealCatalog.todayStyle.
export function todaySurpriseStyle(date = new Date()) {
  const seasonal = seasonalStyle(date);
  if (seasonal) return seasonal;
  const doy = dayOfYearET(date);
  return SURPRISE_STYLES[(doy - 1) % SURPRISE_STYLES.length];
}

// 32-bit FNV-1a over the seed's UTF-8-ish bytes. Question ids + uids are ASCII, so
// charCodeAt matches iOS's `String.utf8` byte-for-byte (lockstep roll).
function stableHash(s) {
  let h = 0x811c9dc5; // 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

// The rare "special" jackpot. Deterministic on `seed` (question id + user id) so a
// given miss always resolves the same rare-or-not — stable across re-renders and
// identical on iOS + web. Returns null ≈ 96.5% of the time.
export function rollRareStyle(seed) {
  if (!RARE_STYLES.length || !seed) return null;
  const h = stableHash(seed);
  if (h % 1000 >= RARE_ODDS_PER_THOUSAND) return null;
  return RARE_STYLES[Math.floor(h / 1000) % RARE_STYLES.length];
}

// The full precedence for a wrong reveal: rare jackpot → seasonal → daily.
// `seed` = questionId + '|' + uid (uid optional; without it the rare tier is off).
// LEGACY: single per-day style. Prefer `dealtSurprise` (distinct per miss).
export function surpriseForReveal(seed = '', date = new Date()) {
  return rollRareStyle(seed) || todaySurpriseStyle(date);
}

// The pool a wrong reveal is dealt from: base styles + the active seasonal style
// (if any) folded IN as one more option (mirrors SurpriseRevealCatalog.dailyPool).
function dailyPool(date = new Date()) {
  const seasonal = seasonalStyle(date);
  return seasonal ? [...SURPRISE_STYLES, seasonal] : SURPRISE_STYLES;
}

// Deterministic Fisher–Yates over the pool, driven by stableHash(seed). Identical
// algorithm to iOS `SurpriseRevealCatalog.seededShuffle` so the dealt style matches
// per card across platforms.
function seededShuffle(pool, seed) {
  const a = pool.slice();
  for (let i = a.length - 1; i >= 1; i--) {
    const j = stableHash(seed + '#' + i) % (i + 1);
    const t = a[i];
    a[i] = a[j];
    a[j] = t;
  }
  return a;
}

// The surprise for ONE wrong reveal. `wrongIds` = the day's miss question ids;
// this question's rank among them (sorted) indexes a per-(user, day) shuffle, so a
// day's misses get DISTINCT styles (no repeats), vary day to day, and stay stable
// on revisit + identical on iOS (SurpriseRevealCatalog.dealtStyle). The ~3.5% rare
// jackpot (seeded per question) still wins when it hits.
export function dealtSurprise({ questionId = '', uid = '', wrongIds = [], date = new Date() } = {}) {
  const questionSeed = (questionId || '') + '|' + (uid || '');
  const rare = rollRareStyle(questionSeed);
  if (rare) return rare;
  const ids = [...wrongIds].sort();
  const daySeed = (uid || '') + '|' + ids.join(',');
  const pool = seededShuffle(dailyPool(date), daySeed);
  if (!pool.length) return SURPRISE_STYLES[0];
  const rank = ids.indexOf(questionId);
  const ordinal = rank < 0 ? 0 : rank;
  return pool[((ordinal % pool.length) + pool.length) % pool.length];
}

export function surpriseStyleById(id) {
  return (
    SURPRISE_STYLES.find((s) => s.id === id) ||
    RARE_STYLES.find((s) => s.id === id) ||
    SEASONAL_STYLES.find((s) => s.id === id) ||
    null
  );
}

function styleById(id) {
  return surpriseStyleById(id);
}

// Deterministic copy pick from a seed string (e.g. the question id) so the line is
// stable across re-renders while still varying from question to question. iOS
// picks randomElement() live; web fixes it per seed.
export function surpriseLine(style, seed = '') {
  if (!style || !style.copy || style.copy.length === 0) return 'The future surprised us.';
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return style.copy[Math.abs(h) % style.copy.length];
}
